import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useParams } from '@tanstack/react-router';
import { Badge, Button, EmptyState, Field, Section, Select, Spinner, Textarea } from '@zt/ui';
import type { Tone } from '@zt/ui';
import { ConfirmButton } from '../components/Confirm';
import { ContentReviewPanel, reviewTone } from '../components/ContentReview';
import { api } from '../lib/api';
import { HelpTip, PageFrame } from '../lib/help';
import { useI18n, useT } from '../lib/i18n';
import { fieldError, useApiMutation } from '../lib/mutation';
import { usePermissions } from '../lib/session';
import type { QuestionDetail, QuestionList } from '../lib/types';
import { StorePicker, useStoreChoice } from './payments';

type Status = 'pending' | 'published' | 'rejected';
const statusTone: Record<Status, Tone> = { pending: 'amber', published: 'moss', rejected: 'slate' };

/** Ürün soruları: yanıt bekleyenler, yayındakiler, reddedilenler. */
export function QuestionsPage() {
  const t = useT();
  const { formatDate } = useI18n();
  const { stores, loading, storeId, setStoreId } = useStoreChoice();
  const [status, setStatus] = useState<Status>('pending');
  const list = useQuery({
    queryKey: ['questions', storeId, status],
    queryFn: () => api.get<QuestionList>(`/v1/admin/questions?storeId=${storeId}&status=${status}`),
    enabled: !!storeId,
  });
  if (loading) return <Spinner label={t('common.loading')} />;
  return (
    <PageFrame helpKey="page.questions">
      {!stores.length ? (
        <EmptyState title={t('admin.payments.noStore')} description={t('admin.payments.noStoreLead')} />
      ) : (
        <>
          <StorePicker stores={stores} storeId={storeId} onChange={setStoreId} />
          <Field label={t('admin.questions.filter')} help={<HelpTip k="questions.filter" />} className="w-60">
            {(p) => (
              <Select {...p} value={status} onChange={(e) => setStatus(e.target.value as Status)}>
                {(['pending', 'published', 'rejected'] as const).map((s) => (
                  <option key={s} value={s}>
                    {t(`admin.questions.statuses.${s}`)}
                  </option>
                ))}
              </Select>
            )}
          </Field>
          {!list.data ? (
            <Spinner label={t('common.loading')} />
          ) : !list.data.length ? (
            <EmptyState title={t(`admin.questions.empty.${status}`)} />
          ) : (
            <ul className="divide-y divide-line rounded-md border border-line bg-surface">
              {list.data.map((q) => (
                <li key={q.id} className="flex flex-wrap items-start gap-x-4 gap-y-2 px-4 py-3 text-sm">
                  <div className="min-w-0 flex-1">
                    <Link
                      to="/sorular/$id"
                      params={{ id: q.id }}
                      className="font-medium text-pine hover:underline"
                    >
                      {q.question.length > 140 ? `${q.question.slice(0, 140)}…` : q.question}
                    </Link>
                    <p className="text-ink-3">
                      {q.productTitle} · {q.authorName} · {formatDate(q.createdAt, true)}
                    </p>
                  </div>
                  <span className="flex flex-wrap gap-1.5">
                    {q.status === 'pending' && (
                      <Badge tone={q.hasAnswer ? 'neutral' : 'amber'}>
                        {q.hasAnswer ? t('admin.questions.answered') : t('admin.questions.unanswered')}
                      </Badge>
                    )}
                    {q.reviewStatus !== 'not_required' && (
                      <Badge tone={reviewTone[q.reviewStatus]}>
                        {t(`admin.pages.reviews.${q.reviewStatus}`)}
                      </Badge>
                    )}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </>
      )}
    </PageFrame>
  );
}

export function QuestionDetailPage() {
  const t = useT();
  const { formatDate } = useI18n();
  const { id } = useParams({ from: '/app/sorular/$id' });
  const { can } = usePermissions();
  const canAnswer = can('catalog.question.answer');
  const q = useQuery({
    queryKey: ['question', id],
    queryFn: () => api.get<QuestionDetail>(`/v1/admin/questions/${id}`),
  });
  const item = q.data;
  const [answer, setAnswer] = useState('');
  const [note, setNote] = useState('');
  useEffect(() => {
    if (item) setAnswer(item.answer ?? '');
  }, [item]);
  const dirty = !!item && answer.trim() !== (item.answer ?? '');
  const inv = { invalidate: [['question', id], ['questions']] };
  const save = useApiMutation(
    () => api.put<QuestionDetail>(`/v1/admin/questions/${id}/answer`, { answer, version: item!.version }),
    { ...inv, success: t('admin.questions.saved') },
  );
  const publish = useApiMutation(
    () => api.post<QuestionDetail>(`/v1/admin/questions/${id}/publish`, { version: item!.version }),
    { ...inv, success: t('admin.questions.publishedToast') },
  );
  const reject = useApiMutation(
    () =>
      api.post<QuestionDetail>(`/v1/admin/questions/${id}/reject`, {
        note: note || null,
        version: item!.version,
      }),
    { ...inv, success: t('admin.questions.rejectedToast') },
  );
  const review = useApiMutation(
    (v: { decision: 'approved' | 'rejected'; note: string }) =>
      api.post<QuestionDetail>(`/v1/admin/questions/${id}/review`, {
        decision: v.decision,
        note: v.note || null,
        contentHash: item!.contentHash,
        version: item!.version,
      }),
    { ...inv, success: t('admin.pages.reviewedToast') },
  );
  if (!item) return <Spinner label={t('common.loading')} />;
  return (
    <PageFrame helpKey="page.question_detail" title={t('admin.questions.detailTitle')}>
      <div className="flex flex-wrap items-center gap-2 text-sm">
        <Link to="/sorular" className="text-pine hover:underline">
          {t('admin.questions.back')}
        </Link>
        <Badge tone={statusTone[item.status]}>{t(`admin.questions.statuses.${item.status}`)}</Badge>
        {item.notifiedAt && (
          <span className="text-ink-2">
            {t('admin.questions.notified', { date: formatDate(item.notifiedAt, true) })}
          </span>
        )}
      </div>
      <Section title={t('admin.questions.question')} className="border-t-0 pt-0">
        <blockquote className="whitespace-pre-line border-s-[3px] border-insert-rule bg-insert p-4 text-[0.9375rem]">
          {item.question}
        </blockquote>
        <p className="mt-2 text-sm text-ink-2">
          {t('admin.questions.meta', { author: item.authorName, date: formatDate(item.createdAt, true) })}
          {item.product && (
            <>
              {' · '}
              {item.product.title}
            </>
          )}
        </p>
      </Section>
      <Section title={t('admin.questions.answer')} description={t('admin.questions.answerLead')}>
        <fieldset disabled={!canAnswer} className="grid max-w-3xl gap-3">
          <Field
            label={t('admin.questions.answerLabel')}
            help={<HelpTip k="questions.answer" />}
            error={fieldError(save.error, 'answer') ?? fieldError(publish.error, 'answer')}
            description={t('admin.pages.chars', { count: answer.length, max: 2000 })}
          >
            {(p) => (
              <Textarea
                {...p}
                rows={6}
                maxLength={2000}
                value={answer}
                onChange={(e) => setAnswer(e.target.value)}
              />
            )}
          </Field>
          {item.answeredByName && item.answeredAt && (
            <p className="text-sm text-ink-3">
              {t('admin.questions.answeredBy', {
                name: item.answeredByName,
                date: formatDate(item.answeredAt, true),
              })}
            </p>
          )}
          <div className="flex flex-wrap items-center gap-2">
            <Button
              onClick={() => save.mutate(undefined)}
              disabled={!dirty || !answer.trim() || save.isPending}
            >
              {t('admin.questions.save')}
            </Button>
            <HelpTip k="questions.save" />
            <Button
              variant="primary"
              onClick={() => publish.mutate(undefined)}
              disabled={dirty || !item.answer || publish.isPending || item.status === 'published'}
            >
              {t('admin.questions.publish')}
            </Button>
            <HelpTip k="questions.publish" />
            {dirty && <span className="text-sm text-ink-2">{t('admin.questions.saveFirst')}</span>}
          </div>
        </fieldset>
      </Section>
      <ContentReviewPanel
        status={item.reviewStatus}
        note={item.reviewNote}
        findings={item.findings}
        required={item.requiresReview}
        dirty={dirty}
        canReview={can('content.expert_review')}
        busy={review.isPending}
        onDecide={(decision, n) => review.mutate({ decision, note: n })}
      />
      {canAnswer && item.status !== 'rejected' && (
        <Section title={t('admin.questions.rejectTitle')} description={t('admin.questions.rejectLead')}>
          <div className="grid max-w-xl gap-2">
            <Field
              label={t('admin.questions.rejectNote')}
              optionalLabel={t('common.optional')}
              help={<HelpTip k="questions.reject" />}
            >
              {(p) => (
                <Textarea
                  {...p}
                  rows={2}
                  maxLength={500}
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                />
              )}
            </Field>
            <div>
              <ConfirmButton
                variant="secondary"
                message={t('admin.questions.rejectConfirm')}
                onConfirm={() => reject.mutateAsync(undefined)}
              >
                {item.status === 'published' ? t('admin.questions.unpublish') : t('admin.questions.reject')}
              </ConfirmButton>
            </div>
          </div>
        </Section>
      )}
      {item.status === 'rejected' && item.moderationNote && (
        <p className="text-sm text-ink-2">
          {t('admin.questions.rejectedNote', { note: item.moderationNote })}
        </p>
      )}
    </PageFrame>
  );
}
